import { defineStore } from 'pinia';
import {
  getMarketPublicOfferSpecification,
  getMarketPublicFpcCodeTranslations,
  getMarketPublicOfferConfiguration,
  getMarketPublicTranslations,
} from '../services/digitalSalesService';
import imagePlaceholder from '../assets/placeholder.png';
import { useConfiguratorApi } from '../composables/configurator';
import { v4 as uuidv4 } from 'uuid';

const { getImagePackage } = useConfiguratorApi();
const getDefaultImages = () => ({
  view: imagePlaceholder,
  front: imagePlaceholder,
  side: imagePlaceholder,
  interior: imagePlaceholder,
  dashboard: imagePlaceholder,
});

export const useMainStore = defineStore('main', {
  state: () => ({
    webpSupported: false,
    initialDataLoaded: false,
    activePage: null,
    offerId: '',
    offerStatus: {},
    offerLoadDataError: false,
    fpcCodeTranslations: [],
    options: null,
    basePrice: null,
    repairMaintenancePrice: null,
    images: getDefaultImages(),
    imageRequestVersion: 0,
    selected: {},
    specification: null,
    token: null,
    locale: null,
    fallbackLocale: 'en-GB',
    translations: {},
    languages: [
      { locale: 'en-GB', name: 'English', description: 'English' },
      { locale: 'pl-PL', name: 'Polish', description: 'Polish' },
      { locale: 'de-DE', name: 'German', description: 'German (Germany)' },
      { locale: 'de-CH', name: 'German', description: 'German (Switzerland)' },
      { locale: 'fr-CH', name: 'French', description: 'French (Switzerland)' },
      { locale: 'it-CH', name: 'Italian', description: 'Italian (Switzerland)' },
      { locale: 'ja-JP', name: 'Japanese', description: 'Japanese' },
    ],
    sections: [
      { id: 'powertrain', name: 'drivelineHeaderTitle' },
      { id: 'cab-color', name: 'cabColourHeaderTitle' },
      { id: 'styling-comfort', name: 'stylingComfortHeaderTitle' },
      { id: 'chassis', name: 'chassisHeaderTitle' },
      { id: 'fifth-wheel', name: 'fifthWheelHeaderTitle' },
      { id: 'safety', name: 'safetyHeaderTitle' },
      { id: 'fuel-efficiency', name: 'fuelEfficiencyHeaderTitle' },
      { id: 'connectedServices', name: 'connectedServicesHeaderTitle' },
      { id: 'repairMaintenance', name: 'repairMaintenanceHeaderTitle' },
    ],
    appLoading: false,
    supportRequested: false,
    isAdmin: false,
    isSuperUser: false,
    isSalesman: false,
    externalStaffReference: null,
    federationUserAccountId: null,
    misc: {},
    selectedPaymentType: 'leasing',
    selectedPaymentCurrency: 'EUR',
    currencies: [],
    selectedNumberVehicles: 1,
    tid: null,
    sectionMetadata: {},
    creationReferenceData: {},
    toasts: [],
    market: null,
    markets: [
      {
        marketId: 'pl',
        description: 'Poland',
        defaultLocale: 'pl-PL',
        defaultHeroImageType: 'view',
        locales: ['pl-PL'],
        showLanguageSelection: false,
        pageTitle: '',
        linksMetadata: [
          { title: 'privacyPolicyText', link: 'https://www.scania.com/pl/pl/home/admin/misc/privacy-statement.html' },
          { title: 'cookiesText', link: 'https://www.scania.com/pl/pl/home/admin/misc/privacy-statement/cookies.html', footer: false },
          { title: 'environmentalPolicyText', link: 'https://www.scania.com/pl/pl/home/admin/misc/scania-environmental-policy.html' },
          { title: 'gdprText', link: 'https://www.scania.com/pl/pl/home/admin/misc/rodo.html' },
        ],
        highlightColor: 'var(--sdds-red-500)',
        showRegulationModal: true,
        vatNipRequiered: true,
        showPrice: true,
        showPriceTooltip: true,
        showRMSliders: true,
        showFiltersOnActiveOffersPage: true,
        showHeroSectionFinancingText: true,
        showStickySupportButton: true,
        showConfigureChevron: true,
      },
      {
        marketId: 'de',
        description: 'Germany',
        defaultLocale:'de-DE',
        defaultHeroImageType: 'view',
        locales: ['de-DE'],
        showLanguageSelection: false,
        pageTitle: '',
        linksMetadata: [
          { title: 'serviceRegulation', link: 'https://www.scania.com/de/de/home/admin/misc/impressum.html' },
          { title: 'privacyPolicyText', link: 'https://www.scania.com/de/de/home/admin/misc/privacy-statement.html' },
          { title: 'cookiesText', link: 'https://www.scania.com/de/de/home/admin/misc/privacy-statement/cookies.html', footer: false },
        ],
        highlightColor: 'var(--sdds-blue-400)',
        showRegulationModal: false,
        vatNipRequiered: false,
        showPrice: true,
        showPriceTooltip: true,
        showRMSliders: true,
        showFiltersOnActiveOffersPage: true,
        showHeroSectionFinancingText: true,
        showStickySupportButton: true,
        showConfigureChevron: true,
      },
      {
        marketId: 'ch',
        description: 'Switzerland',
        defaultLocale:'de-CH',
        defaultHeroImageType: 'view',
        locales:['de-CH', 'fr-CH', 'it-CH'],
        showLanguageSelection: true,
        pageTitle: '',
        linksMetadata: [
          { title: 'privacyPolicyText', link: 'https://www.scania.com/ch/de/home/admin/misc/privacy-statement.html' },
          { title: 'cookiesText', link: 'https://www.scania.com/ch/de/home/admin/misc/privacy-statement/cookies.html', footer: false },
          { title: 'environmentalPolicyText', link: 'https://www.scania.com/ch/de/home/about-scania/sustainability/environmental-footprint.html' },
        ],
        highlightColor: 'var(--sdds-blue-400)',
        showRegulationModal: false,
        vatNipRequiered: false,
        showPrice: true,
        showPriceTooltip: false,
        showRMSliders: true,
        showFiltersOnActiveOffersPage: true,
        showHeroSectionFinancingText: true,
        showStickySupportButton: true,
        showConfigureChevron: true,
      },
      {
        marketId: 'jp',
        description: 'Japan',
        defaultLocale:'ja-JP',
        defaultHeroImageType: 'view',
        locales:['ja-JP', 'en-GB'],
        showLanguageSelection: true,
        pageTitle: 'Prime Ready',
        linksMetadata: [
          { title: 'privacyPolicyText', link: 'https://www.scania.com/jp/ja/home/admin/misc/privacy-statement.html' },
          { title: 'cookiesText', link: 'https://www.scania.com/jp/ja/home/admin/misc/privacy-statement/cookies.html', footer: false },
          // { title: 'sustainabilityText', link: 'https://www.scania.com/jp/ja/home/about-scania/sustainability.html' },
        ],
        highlightColor: 'var(--sdds-blue-400)',
        showRegulationModal: false,
        vatNipRequiered: false,
        showPrice: false,
        showPriceTooltip: false,
        showRMSliders: false,
        showFiltersOnActiveOffersPage: false,
        showHeroSectionFinancingText: false,
        showStickySupportButton: false,
        showConfigureChevron: false,
      },
    ],
    showContactRequestModal: false,
    contactRequested: false,
    showYearEndOfferBanner: false,
    frameAgreementAvailable: null,
    featureToggleData: {},
    vehicleCardImage: {},
    serviceSavings: [],
  }),
  getters: {
    getSelected(state) {
      return state.selected;
    },
    getModifiedSpecification(state) {
      const specImpacts = this.getSpecImpactGrouped(state.selected).map(x => x.specImpact).flat(1);
      return state.specification?.map(item => ({
        ...item,
        selections: item.selections.map(subItem => {
          const impact = subItem.id === 'cabColour'
            ? specImpacts.find(x => x.id === subItem.id && !x.value.endsWith('-'))
            : specImpacts.find(x => x.id === subItem.id);
          return impact ? { id: impact.id, fpcCode: impact.value } : subItem;
        }),
      }));
    },
    getOfferStatus(state) {
      return state.offerStatus;
    },
    getLocale () {
      return this.locale || this.getDefaultLocale;
    },
    getDefaultLocale() {
      const market = this.markets.find(m=>m.marketId === this.market);
      return market?.defaultLocale || this.fallbackLocale;
    },
    getLanguages() {
      const market = this.markets.find(m=>m.marketId === this.market);
      const locales = market?.locales || [this.fallbackLocale];
      return this.languages.filter(l => locales.includes(l.locale));
    },
    cookiesPrivacyLinkForActiveMarket(state) {
      // Return the specific cookies privacy link for the active market (default to the global privacy link)
      const link = state.markets.find(market => market.marketId === state.market)?.linksMetadata?.find(link => link.title === 'cookiesText')?.link;
      return link ?? 'https://www.scania.com/group/en/home/admin/misc/privacy-statement/cookies.html';
    },
    footerLinkMetadataForActiveMarket(state) {
      // Return all link metadata for active market that do not have "footer" set to false
      return state.markets.find(market => market.marketId === state.market)?.linksMetadata?.filter(item => item.footer !== false) ?? [];
    },
    activeMarket(state) {
      // Return the active market configuration
      return state.markets.find(market => market.marketId === state.market) ?? null;
    },
    shouldShowYear2024OfferBanner() {
      // Do not show banner if a frame agreement is being configured
      if (this.misc?.frameAgreement) {
        return false;
      }

      // Do not show banner if a frame agreement is available and the home / active offers page is open
      if (this.frameAgreementAvailable !== false && (this.activePage ===null || this.activePage === 'HOME' || this.activePage === 'ACTIVE_OFFERS')) {
        return false;
      }

      // Parse the feature toggle data
      const parsedFeatureToggle = this.featureToggleData[`${this.market}#webapp_year2024OfferBanner`];

      // Return the isActive flag if parse succeeded, otherwise false
      return parsedFeatureToggle ? parsedFeatureToggle.isActive && parsedFeatureToggle.isFeatureDateValid : false;
    },
  },
  actions: {
    setOfferId(id) {
      this.offerId = id;
    },
    getTranslation(key, locale = this.locale) {
      const marketKey =  this.market + String(key)?.charAt(0)?.toUpperCase() + String(key)?.slice(1);
      return this.translations[locale]?.[key] ||
        this.translations[locale]?.[marketKey] ||
        this.translations[this.fallbackLocale]?.[key] ||
        this.translations[this.fallbackLocale]?.[marketKey] ||
        key;
    },
    getSpecImpactGrouped(selections) {
      return Object.entries(selections)
      .map(([groupKey, group]) => {
        if (['leasing', 'payment', 'driverServices', 'connectedServices', 'highlights'].includes(groupKey)) return [];
        return Object.entries(group).map(([key, selection]) => ({ key, specImpact: selection.specImpact }));
      })
      .flat(1);
    },
    updateSelected(property, value) {
      this.selected[property] = value;
    },
    async loadTranslations() {
      if (this.translations[this.locale]) return;
      const data = (await getMarketPublicTranslations(this.locale)).data;
      this.translations = { ...this.translations, [this.locale]: data };
    },
    loadSpecification() {
      getMarketPublicOfferSpecification()
      .then(response => {
        this.specification = response.data;
        this.loadSpecificationTranslations();
      });
    },
    loadSpecificationTranslations() {
      const fpcCodes = this.specification.reduce((finalList, category) => {
        const categoryList = category.selections.reduce((categoryList, item) => [...categoryList, item.fpcCode], []);
        return [...finalList, ...categoryList];
      }, []).filter(Boolean);
      getMarketPublicFpcCodeTranslations(fpcCodes, this.locale)
      .then(response => {
        this.fpcCodeTranslations.push(...response.data);
      }).catch((e) => {
        console.error(e);
      });
    },
    async loadConfiguration(ref) {
      this.selected = {};
      this.offerLoadDataError = false;
      this.imageRequestVersion += 1;
      this.images = getDefaultImages();
      let response = null;
      try {
        response = await getMarketPublicOfferConfiguration(this.market, this.offerId, ref);
      } catch (error) {
        this.offerLoadDataError = true;
        return;
      }
      this.offerStatus = { expired: response.data.expired, finalized: response.data.finalized, isOfferNotValid: response.data.isOfferNotValid };
      if (Object.values(this.offerStatus).some(Boolean)) {
        console.log('Offer is not available. Status: ', this.offerStatus);
        return;
      }
      this.options = response.data.options;
      this.basePrice = response.data.basePrice;
      this.repairMaintenancePrice = response.data.repairMaintenancePrice;
      this.misc = response.data.misc;
      this.selectedPaymentType = response.data.options.payment?.find(o => o.id === 'paymentType')?.options?.find(o => o.default)?.value ?? 'leasing';
      this.selectedPaymentCurrency = response.data.options.payment?.find(o => o.id === 'paymentCurrency')?.options?.find(o => o.default)?.id ?? 'EUR';
      this.currencies = response.data.options.payment?.find(o => o.id === 'paymentCurrency')?.options ?? [];
      this.setConfigurationDefaults();
      this.loadConfigurationTranslations();
      this.sectionMetadata = response.data.sectionMetadata;
      this.creationReferenceData = response.data.creationReferenceData || {};
      this.serviceSavings = response.data.serviceSavings || [];
      return;
    },
    loadConfigurationTranslations() {
      const optionKeys = Object.keys(this.options || {});
      const fpcCodes = [];
      optionKeys.forEach(key => {
        this.options[key].forEach(item => {
          item.options.forEach(option => {
            const tempCodes = option.specImpact.flatMap(x =>
              (x.value && !x.value.endsWith('-'))
              ? [x.value]
              : [],
            );
            fpcCodes.push(...tempCodes);
          });
        });
      });
      const uniqueCodes = [...new Set(fpcCodes)];
      getMarketPublicFpcCodeTranslations(uniqueCodes, this.locale)
      .then(response => {
        this.fpcCodeTranslations.push(...response.data);
      }).catch((e) => {
        console.error(e);
      });
    },
    setConfigurationDefaults() {
      const optionKeys = Object.keys(this.options);
      optionKeys.forEach(key => {
        this.options[key].forEach(item => {
          const defaultVal = item.options.find(x => x.default);
          if (!this.selected[key]) this.selected[key] = {};
          this.selected[key][item.id] = defaultVal || null;
        });
      });
      this.updateImages(this.selected);
    },
    updateImages(options) {
      const requestVersion = ++this.imageRequestVersion;
      getImagePackage(this.getSpecImpactGrouped(options))
      .then(response => {
        // Ignore outdated responses so older requests cannot overwrite newer selections.
        if (requestVersion !== this.imageRequestVersion) return;
        this.images = { ...getDefaultImages(), ...response };
      })
      .catch((error) => {
        if (requestVersion !== this.imageRequestVersion) return;
        console.error('updateImages-error', error);
      });
    },
    addToast(toast) {
      toast.id = uuidv4();
      this.toasts.push(toast);
    },
    addSuccessToast(toast, options) {
      if (typeof toast === 'string') {
        toast = {
          header: toast,
        };
      }
      this.addToast({
        ...toast,
        translate: options?.translate,
        type: 'success',
      });
    },
    addErrorToast(toast, options) {
      if (typeof toast === 'string') {
        toast = {
          header: toast,
        };
      }
      this.addToast({
        ...toast,
        translate: options?.translate,
        type: 'error',
      });
    },
    clearToast(id) {
      this.toasts = this.toasts.filter(x => x.id !== id);
    },
  },
});
